<template>
    <div class="flex flex-col border rounded w-full p-4"
         :class="showChannel ? (alternateColors ? 'border-orange-400' : 'border-blue-500') : ''"
    >

        <div class="flex flex-row justify-between w-full">
            <h2 class="text-base p-0 mb-0">Channel {{ channel }}</h2>
            <toggle v-model:value="showChannel" v-bind="alternateColors ? { color: 'orange-400' } : {}" />
        </div>

        <div v-if="showChannel" class="flex flex-col space-y-4 mt-4 select-none">
            <div class="flex flex-col">
                <div class="flex flex-col items-start">
                    <h3 class="text-base p-0 mb-0">Open Direction</h3>
                    <p class="text-sm text-gray-500">Electrical state that represents "open"</p>
                </div>
                <div class="inline-flex" role="radiogroup">
                    <label class="cursor-pointer">
                        <input type="radio" :name="'logic-' + uid" value="high" class="sr-only peer" v-model="selectedOpenDirection">
                        <div :class="[
                            'px-6 py-1 border font-normal',
                            'rounded-l-md',
                            alternateColors
                              ? 'border-orange-200 text-orange-500 peer-checked:bg-orange-400 peer-checked:border-orange-400'
                              : 'border-blue-300 text-blue-600 peer-checked:bg-blue-500 peer-checked:border-blue-500',
                            'peer-checked:text-white'
                          ]">
                            Logic High
                        </div>
                    </label>
                    <label class="cursor-pointer">
                        <input type="radio" :name="'logic-' + uid" value="low" class="sr-only peer" v-model="selectedOpenDirection">
                        <div :class="[
                            'px-6 py-1 border font-normal',
                            'rounded-r-md',
                            alternateColors
                              ? 'border-orange-200 text-orange-500 peer-checked:bg-orange-400 peer-checked:border-orange-400'
                              : 'border-blue-300 text-blue-600 peer-checked:bg-blue-500 peer-checked:border-blue-500',
                            'peer-checked:text-white'
                          ]">
                            Logic Low
                        </div>
                    </label>
                </div>
            </div>

            <div class="flex flex-col">
                <div class="flex flex-col items-start">
                    <h3 class="text-base p-0 mb-0">Alarming</h3>
                    <p class="text-sm text-gray-500">Enable alarm notifications for this channel</p>
                </div>
                <toggle v-model:value="shouldAlarm" v-bind="alternateColors ? { color: 'orange-400' } : {}" />
            </div>

            <div v-if="shouldAlarm" class="flex flex-col">
                <div class="flex flex-col items-start">
                    <h3 class="text-base p-0 mb-0">Alarm Trigger</h3>
                    <p class="text-sm text-gray-500">Trigger alarm when contact is</p>
                </div>
                <div class="inline-flex" role="radiogroup">
                    <label class="cursor-pointer">
                        <input type="radio" :name="'trigger-' + uid" value="open" class="sr-only peer" v-model="selectedAlarmTrigger">
                        <div :class="[
                            'px-6 py-1 border font-normal',
                            'rounded-l-md',
                            alternateColors
                              ? 'border-orange-200 text-orange-500 peer-checked:bg-orange-400 peer-checked:border-orange-400'
                              : 'border-blue-300 text-blue-600 peer-checked:bg-blue-500 peer-checked:border-blue-500',
                            'peer-checked:text-white'
                          ]">
                            Open
                        </div>
                    </label>
                    <label class="cursor-pointer">
                        <input type="radio" :name="'trigger-' + uid" value="closed" class="sr-only peer" v-model="selectedAlarmTrigger">
                        <div :class="[
                            'px-6 py-1 border font-normal',
                            'rounded-r-md',
                            alternateColors
                              ? 'border-orange-200 text-orange-500 peer-checked:bg-orange-400 peer-checked:border-orange-400'
                              : 'border-blue-300 text-blue-600 peer-checked:bg-blue-500 peer-checked:border-blue-500',
                            'peer-checked:text-white'
                          ]">
                            Closed
                        </div>
                    </label>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import Toggle from "@/components/elements/toggle.vue";
import { ref, watch } from "vue";

const uid = `logic-${Math.random().toString(36).substring(2, 9)}`;

const props = defineProps({
    channel: Number,
    modelValue: Boolean,
    highIsOpen: Boolean,
    alarmsEnabled: Boolean,
    alarmOnOpen: Boolean,
    alternateColors: {
      type: Boolean,
      default: false
    }
});

const emit = defineEmits([
    'update:modelValue',
    'update:highIsOpen',
    'update:alarmsEnabled',
    'update:alarmTrigger'
]);

const showChannel = ref(props.modelValue);
const selectedOpenDirection = ref(props.highIsOpen ? 'high' : 'low');
const shouldAlarm = ref(props.alarmsEnabled);
const selectedAlarmTrigger = ref(props.alarmOnOpen ? 'open' : 'closed');

watch(() => props.modelValue, val => {
  if (val !== showChannel.value) {
    showChannel.value = val;
  }
});
watch(() => props.highIsOpen, val => {
    const logic = val ? 'high' : 'low';
    if (logic !== selectedOpenDirection.value) {
        selectedOpenDirection.value = logic;
    }
});
watch(() => props.alarmsEnabled, val => {
    if (val !== shouldAlarm.value) {
        shouldAlarm.value = val;
    }
});
watch(() => props.alarmOnOpen, val => {
    const trigger = val ? 'open' : 'closed';
    if (trigger !== selectedAlarmTrigger.value) {
        selectedAlarmTrigger.value = trigger;
    }
});

watch(showChannel, val => emit('update:modelValue', val));
watch(selectedOpenDirection, val => emit('update:highIsOpen', val === 'high'));
watch(shouldAlarm, val => emit('update:alarmsEnabled', val));
watch(selectedAlarmTrigger, val => emit('update:alarmTrigger', val === 'open'));
</script>